/**
 * nexseed's own error and not-found screens.
 *
 * Without these the router falls back to its built-in "Something went wrong!" panel, which is
 * unbranded and shows a raw stack to plant staff. Both screens share one frame: the nexseed mark, a
 * small seed-tray illustration drawn in brand tokens (so light and dark themes both work, and no
 * image or package is added), a plain-language headline, and a way back.
 *
 * The error screen keeps the technical message behind a closed disclosure as selectable text rather
 * than offering a copy button: the app is served over plain http, where the clipboard API is not
 * available, so staff read it out or photograph it.
 */
import { formatDateTime } from "@nexseed/shared";
import {
  Link,
  useRouter,
  type ErrorComponentProps,
} from "@tanstack/react-router";
import { RotateCcw, Sprout } from "lucide-react";
import { useState, type ReactNode } from "react";
import { Button } from "@/components/ui/button";

const soil = { fill: "var(--brand-soft)", stroke: "var(--brand-border)" };
const leaf = { fill: "var(--brand)" };
const stem = { stroke: "var(--brand-strong)" };
const wilt = { stroke: "var(--ink-muted)" };

/** The tray every illustration stands in: three cells of soil. */
function Tray({ children }: { children: ReactNode }) {
  return (
    <svg
      viewBox="0 0 240 150"
      className="mx-auto mb-6 h-36 w-auto"
      role="img"
      aria-hidden="true"
    >
      <ellipse
        cx="120"
        cy="138"
        rx="100"
        ry="8"
        style={{ fill: "var(--surface-sunken)" }}
      />
      <rect
        x="22"
        y="92"
        width="196"
        height="40"
        rx="10"
        style={soil}
        strokeWidth="2"
      />
      {[46, 120, 194].map((cx) => (
        <ellipse
          key={cx}
          cx={cx}
          cy="96"
          rx="28"
          ry="6"
          style={{ fill: "var(--brand-border)", opacity: 0.55 }}
        />
      ))}
      {children}
    </svg>
  );
}

function Sprouting({ x }: { x: number }) {
  return (
    <g>
      <path
        d={`M${x} 95 C${x} 80 ${x} 70 ${x} 58`}
        style={stem}
        strokeWidth="3"
        fill="none"
        strokeLinecap="round"
      />
      <path
        d={`M${x} 70 C${x - 20} 66 ${x - 22} 52 ${x - 18} 46 C${x - 6} 50 ${x} 58 ${x} 70 Z`}
        style={leaf}
      />
      <path
        d={`M${x} 64 C${x + 18} 58 ${x + 22} 44 ${x + 16} 38 C${x + 4} 44 ${x} 52 ${x} 64 Z`}
        style={leaf}
      />
    </g>
  );
}

/** A tray whose middle seedling has wilted, tagged like a QC slip. */
function WiltedTray() {
  return (
    <Tray>
      <Sprouting x={46} />
      <Sprouting x={194} />
      <path
        d="M120 95 C120 80 124 70 136 66"
        style={wilt}
        strokeWidth="3"
        fill="none"
        strokeLinecap="round"
      />
      <path
        d="M136 66 C146 66 152 74 150 84 C142 82 136 76 136 66 Z"
        style={{ fill: "var(--ink-muted)", opacity: 0.7 }}
      />
      <path
        d="M128 70 C118 62 104 66 100 74 C110 78 122 76 128 70 Z"
        style={{ fill: "var(--ink-muted)", opacity: 0.5 }}
      />
      {/* The QC slip tag, tied to the stem. */}
      <path
        d="M124 78 L160 24"
        style={{ stroke: "var(--border-strong)" }}
        strokeWidth="1.5"
        strokeDasharray="3 3"
      />
      <g transform="rotate(-8 176 22)">
        <rect
          x="150"
          y="8"
          width="56"
          height="30"
          rx="5"
          style={{ fill: "var(--surface-raised)", stroke: "var(--danger)" }}
          strokeWidth="2"
        />
        <circle cx="158" cy="23" r="3" style={{ fill: "var(--danger)" }} />
        <text
          x="184"
          y="28"
          textAnchor="middle"
          fontSize="14"
          fontWeight="700"
          style={{ fill: "var(--danger-text)", fontFamily: "var(--font-mono)" }}
        >
          ERR
        </text>
      </g>
    </Tray>
  );
}

/** A tray with its middle cell still bare: nothing was planted at this address. */
function EmptyTray() {
  return (
    <Tray>
      <Sprouting x={46} />
      <Sprouting x={194} />
      <ellipse
        cx="120"
        cy="84"
        rx="24"
        ry="24"
        style={{ fill: "none", stroke: "var(--brand-border)" }}
        strokeWidth="2"
        strokeDasharray="5 5"
      />
      <ellipse
        cx="120"
        cy="90"
        rx="5"
        ry="3.5"
        style={{ fill: "var(--ink-muted)", opacity: 0.6 }}
        transform="rotate(-20 120 90)"
      />
      <text
        x="120"
        y="56"
        textAnchor="middle"
        fontSize="18"
        fontWeight="700"
        style={{ fill: "var(--brand-text)" }}
      >
        ?
      </text>
    </Tray>
  );
}

function Frame({
  art,
  eyebrow,
  title,
  body,
  actions,
  details,
}: {
  art: ReactNode;
  eyebrow: string;
  title: string;
  body: string;
  actions: ReactNode;
  details?: ReactNode;
}) {
  return (
    <main className="grid h-full min-h-[70vh] place-items-center bg-surface px-6 py-12">
      <div className="w-full max-w-lg text-center">
        <div className="mb-8 flex items-center justify-center gap-2 text-sm font-semibold text-ink">
          <span className="grid h-7 w-7 place-items-center rounded-lg bg-primary text-primary-ink">
            <Sprout className="h-4 w-4" />
          </span>
          NexSeed
        </div>
        {art}
        <p className="text-xs font-semibold tracking-[0.18em] text-primary-text uppercase">
          {eyebrow}
        </p>
        <h1 className="mt-2 text-2xl font-semibold text-balance text-ink">
          {title}
        </h1>
        <p className="mt-3 text-sm text-pretty text-ink-muted">{body}</p>
        <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
          {actions}
        </div>
        {details}
      </div>
    </main>
  );
}

/** Any route that throws while rendering or loading lands here instead of the router's own panel. */
export function AppErrorPage({ error, reset }: ErrorComponentProps) {
  const router = useRouter();
  // Captured once, so the time on screen is when it broke rather than when it last re-rendered.
  const [at] = useState(() => formatDateTime(new Date()));
  const path = `${window.location.pathname}${window.location.search}`;
  const message = error instanceof Error ? error.message : String(error);
  const stack =
    import.meta.env.DEV && error instanceof Error ? error.stack : undefined;

  return (
    <Frame
      art={<WiltedTray />}
      eyebrow="Something broke"
      title="This screen didn't germinate."
      body="Something went wrong while loading this page. Nothing you entered was saved or lost — try again, or head back to the dashboard."
      actions={
        <>
          <Button
            onClick={() => {
              void router.invalidate();
              reset();
            }}
          >
            <RotateCcw className="h-4 w-4" />
            Try again
          </Button>
          <Button variant="secondary" asChild>
            <Link to="/">Go to home</Link>
          </Button>
        </>
      }
      details={
        <details className="mt-10 rounded-lg border border-border bg-surface-raised px-4 py-3 text-left text-xs">
          <summary className="cursor-pointer font-medium text-ink-secondary select-none">
            Technical details
          </summary>
          <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-ink-muted">
            <dt className="font-medium text-ink-secondary">Error</dt>
            <dd className="font-mono break-words text-danger-text">
              {message}
            </dd>
            <dt className="font-medium text-ink-secondary">Page</dt>
            <dd className="font-mono break-all">{path}</dd>
            <dt className="font-medium text-ink-secondary">Time</dt>
            <dd className="font-mono">{at}</dd>
          </dl>
          {stack && (
            <pre className="mt-3 max-h-48 overflow-auto rounded bg-surface-sunken p-2 font-mono text-2xs whitespace-pre-wrap text-ink-muted">
              {stack}
            </pre>
          )}
        </details>
      }
    />
  );
}

/** An address that matches no screen. */
export function NotFoundPage() {
  return (
    <Frame
      art={<EmptyTray />}
      eyebrow="Page not found"
      title="Nothing planted here."
      body="This address doesn't lead to a NexSeed screen. It may have moved, or the link was mistyped."
      actions={
        <Button asChild>
          <Link to="/">Go to home</Link>
        </Button>
      }
    />
  );
}
